import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { listGardens } from "@/lib/api";
import { useAuth } from "./AuthContext";

const GardenContext = createContext(null);
const STORAGE_KEY = "vm_active_garden_id";

/**
 * Shared state for the active garden + the user's full garden list.
 * Active id is persisted in localStorage. If the stored id is invalid
 * (deleted or belongs to another account), we fall back to the first garden.
 */
export function GardenProvider({ children }) {
    const { user } = useAuth();
    const [gardens, setGardens] = useState([]);
    const [activeGardenId, setActiveGardenIdState] = useState(() =>
        typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null
    );
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!user || user === false) {
            setGardens([]);
            return;
        }
        setLoading(true);
        try {
            const list = await listGardens();
            setGardens(list);
            // Auto-fix invalid active id
            if (list.length === 0) {
                setActiveGardenIdState(null);
                localStorage.removeItem(STORAGE_KEY);
            } else if (!activeGardenId || !list.find((g) => g.id === activeGardenId)) {
                setActiveGardenIdState(list[0].id);
                localStorage.setItem(STORAGE_KEY, list[0].id);
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Re-fetch on auth change (login / logout)
    useEffect(() => {
        if (user && user !== false) refresh();
        else setGardens([]);
    }, [user, refresh]);

    const setActiveGarden = useCallback((id) => {
        setActiveGardenIdState(id);
        if (id) localStorage.setItem(STORAGE_KEY, id);
        else localStorage.removeItem(STORAGE_KEY);
    }, []);

    const activeGarden = useMemo(
        () => gardens.find((g) => g.id === activeGardenId) || null,
        [gardens, activeGardenId]
    );

    const value = useMemo(
        () => ({ gardens, activeGardenId, activeGarden, setActiveGarden, refresh, loading }),
        [gardens, activeGardenId, activeGarden, setActiveGarden, refresh, loading]
    );

    return <GardenContext.Provider value={value}>{children}</GardenContext.Provider>;
}

export function useGardens() {
    const ctx = useContext(GardenContext);
    if (!ctx) throw new Error("useGardens must be used inside <GardenProvider>");
    return ctx;
}
